// @ts-expect-error - TS2305 - Module '"workbox-window"' has no exported member 'WorkboxEvent'.
import { type Workbox, messageSW, type WorkboxEvent } from 'workbox-window';

import { log } from '@atlassian/jira-common-util-logging';
import { PACKAGE_LOCATION } from '../../common/constants';
import { getAnalyticsInstance } from './analytics';
import type {
	ServiceWorkerInfoResponse,
	WorkerInfo,
	WorkerLifecycleAnalyticsEvent,
	WorkerQuery,
	RegistrationData,
} from './types';
import { getStorage, getCurrentTime } from './utils';

export type { ServiceWorkerInfoResponse };

export const REQUESTS_STORAGE_KEY = 'service-worker-registration-requests';

/**
 * Get this tab's BUILD_KEY
 */
const getBuildKey = () => (typeof window !== 'undefined' ? window.BUILD_KEY : 'UNKNOWN');

/**
 * Send a SERVICE_WORKER_INFO message to a worker and return a promise to its response.
 */
const getServiceWorkerInfo = (currentWorker: ServiceWorker): Promise<ServiceWorkerInfoResponse> =>
	messageSW(currentWorker, { type: 'SERVICE_WORKER_INFO' });

/**
 * Send a message into a worker to get its version and type.
 */
const getWorkerInfo = async (currentWorker: ServiceWorker): Promise<WorkerInfo> => {
	const start = performance.now();
	const response = await getServiceWorkerInfo(currentWorker);
	const latency = performance.now() - start;
	return {
		version: response.version,
		workerType: response.serviceWorker,
		latency,
		numberOfControlledClients: response.numberOfControlledClients,
		numberOfClients: response.numberOfClients,
		hasMultipleTabs: response.hasMultipleTabs,
	};
};

/**
 * Inspect the current service worker state:
 *
 * * Store the current time
 * * Check for a worker controlling the window
 * * Check for the worker passed-in as an argument (normally from an event)
 * * Fetch version/type information from each of these
 */
export const queryWorker = async (worker: null | ServiceWorker): Promise<WorkerQuery> => {
	const now = performance.now();
	const controller = navigator.serviceWorker?.controller;
	const hasController = controller != null;
	const isController = worker === controller;
	const hasWorker = worker != null;
	const pageVersion = getBuildKey();
	const controllerInfo = controller != null ? await getWorkerInfo(controller) : null;
	const workerInfo = worker != null ? await getWorkerInfo(worker) : null;

	return {
		now,
		pageVersion,
		hasController,
		isController,
		hasWorker,
		controllerInfo,
		workerInfo,
	};
};

type RegistrationItem = {
	createdAt: number;
	updatedAt: number;
	registrations: {
		previous: RegistrationData | null;
		current: RegistrationData | null;
	};
};

/**
 * Reads the previous registration attempt version & timestamp from localStorage. Stores the current
 * registration attempt in the same entry by swapping it with the last current attempt.
 */
export const getRegistrationData = (): RegistrationItem | null => {
	try {
		const storage = getStorage();
		const item = storage.get(REQUESTS_STORAGE_KEY);
		const currentTime = getCurrentTime();
		const registrationData: RegistrationItem = item ?? {
			createdAt: currentTime,
			updatedAt: null,
			registrations: { previous: null, current: null },
		};
		registrationData.updatedAt = getCurrentTime();
		registrationData.registrations.previous = registrationData.registrations.current;
		registrationData.registrations.current = {
			time: currentTime,
			version: getBuildKey(),
		};
		storage.set(REQUESTS_STORAGE_KEY, registrationData);
		return registrationData;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		log.safeErrorWithoutCustomerData(PACKAGE_LOCATION, err);
		return null;
	}
};

/**
 * Sends a `serviceWorker entered` event, indicating that a service-worker is about to be registered.
 *
 * This contains the currently controlling service-worker version if any.
 */
export const logCurrentWorkerMetrics = async (
	startTime: number,
	isInScope: boolean,
): Promise<void> => {
	const query = await queryWorker(navigator.serviceWorker?.controller ?? null);
	const client = await getAnalyticsInstance();
	const registrationData = getRegistrationData();

	client.sendOperationalEvent({
		action: 'entered',
		actionSubject: 'serviceWorker',
		source: PACKAGE_LOCATION,
		attributes: {
			startTime,
			isInScope,
			...query,
			registrationData: registrationData
				? {
						previous: registrationData.registrations.previous,
					}
				: null,
		},
	});
};

/**
 * Publish a lifecycle event corresponding to a workbox event.
 */
export const sendServiceWorkerLifecycleEvent = (
	event: WorkboxEvent,
	startTime: number,
): Promise<void> => {
	const run = async () => {
		if (event.type === 'message') {
			return;
		}

		const query = await queryWorker(event.sw);
		const client = await getAnalyticsInstance();

		const analyticsEvent: WorkerLifecycleAnalyticsEvent = {
			action: event.type,
			actionSubject: 'serviceWorker',
			source: PACKAGE_LOCATION,
			attributes: {
				startTime,
				...query,
				isUpdate: event.isUpdate != null ? event.isUpdate : null,
				isExternal: event.isExternal != null ? event.isExternal : null,
				wasWaitingBeforeRegister:
					event.wasWaitingBeforeRegister != null ? event.wasWaitingBeforeRegister : null,
				registrationData: null,
			},
		};
		client.sendOperationalEvent(analyticsEvent);
	};

	return run().catch((err) => {
		log.safeErrorWithoutCustomerData(
			PACKAGE_LOCATION,
			'Failed to fire ServiceWorker lifecycle analytics',
			err,
		);
	});
};

/**
 * For this Jira SPA worker registration, publish events on every lifecycle step.
 */
export const attachMetricListeners = (workbox: Workbox, startTime: number) => {
	workbox.addEventListener('activated', (event) => {
		sendServiceWorkerLifecycleEvent(event, startTime);
	});
	workbox.addEventListener('waiting', (event) => {
		sendServiceWorkerLifecycleEvent(event, startTime);
	});
	workbox.addEventListener('installed', (event) => {
		sendServiceWorkerLifecycleEvent(event, startTime);
	});
	workbox.addEventListener('controlling', (event) => {
		sendServiceWorkerLifecycleEvent(event, startTime);
	});
	workbox.addEventListener('redundant', (event) => {
		sendServiceWorkerLifecycleEvent(event, startTime);
	});
};
